import { useRef, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Command } from "cmdk";
import { Check, Plus, Tag } from "lucide-react";
import { useTranslation } from "react-i18next";
import { unwrap } from "@tern/api-client";
import { api, errorMessage } from "@/lib/api";
import { useProjectStore, type ProjectData } from "@/lib/store";
import { toast } from "@/lib/toast";
import { LabelDot } from "./PropertyIcons";
import { PropertyChip } from "./PropertyChip";

const COLORS = ["#c7ad7d", "#7f9fb8", "#85ad94", "#b494ba", "#c58d81", "#969aa6"];

export function TagPicker({ d, value, onChange, disabled, compact, onBusyChange, open: controlledOpen, onOpenChange }: {
  d: ProjectData; value: string[]; onChange: (ids: string[]) => void | Promise<unknown>; disabled?: boolean; compact?: boolean;
  onBusyChange?: (busy: boolean) => void;
  open?: boolean; onOpenChange?: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const labels = useProjectStore((s) => s.byId[d.project.id]?.labels ?? d.labels);
  const [internalOpen, setInternalOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [color, setColor] = useState(COLORS[0]!);
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const all = Object.values(labels).filter((tag) => !tag.deleted_at).sort((a, b) => a.name.localeCompare(b.name));
  const selected = all.filter((tag) => value.includes(tag.id));
  const name = query.trim();
  const canCreate = name && new TextEncoder().encode(name).length <= 40 && !all.some((tag) => tag.name.toLocaleLowerCase() === name.toLocaleLowerCase());
  const change = async (ids: string[]) => {
    if (pending.current) return;
    pending.current = true; setBusy(true); onBusyChange?.(true);
    try { await onChange(ids); } catch (e) { toast(errorMessage(e), { tone: "error" }); }
    finally { pending.current = false; setBusy(false); onBusyChange?.(false); }
  };
  const create = async () => {
    if (!canCreate || pending.current) return;
    pending.current = true; setBusy(true); onBusyChange?.(true);
    try {
      const tag = unwrap(await api.POST("/api/workspaces/{slug}/projects/{key}/labels", { params: { path: { slug: d.slug, key: d.project.key } }, body: { name, color } }));
      useProjectStore.getState().applyLabel(tag);
      await onChange([...value, tag.id]);
      setQuery("");
    } catch (e) { toast(errorMessage(e), { tone: "error" }); }
    finally { pending.current = false; setBusy(false); onBusyChange?.(false); }
  };
  return <Popover.Root open={open} onOpenChange={(next) => { if (!busy) { setOpen(next); if (!next) setQuery(""); } }}>
    <Popover.Trigger asChild>
      <PropertyChip disabled={disabled} title={t("issue.labels")} className={compact ? "h-6 border-transparent bg-transparent px-1" : ""}>
        {selected.length === 1 ? <LabelDot color={selected[0]!.color} /> : <Tag className="h-3.5 w-3.5 shrink-0" />}
        {(!compact || selected.length > 0) && <span className="max-w-[180px] truncate">{selected.length === 1 ? selected[0]!.name : t("issue.labels")}</span>}
        {selected.length > 1 && <span className="text-[11px] text-faint">{selected.length}</span>}
      </PropertyChip>
    </Popover.Trigger>
    <Popover.Portal>
      <Popover.Content align="start" sideOffset={8} collisionPadding={12} className="z-[60] w-[300px] max-w-[calc(100vw-24px)] overflow-hidden rounded-xl border border-border-strong bg-elevated shadow-xl" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <Command loop>
          <Command.Input value={query} onValueChange={setQuery} disabled={busy} placeholder={t("tags.search")} aria-label={t("tags.search")} className="h-11 w-full border-b border-border bg-transparent px-3 text-[13px] outline-none" />
          <Command.List className="max-h-[240px] overflow-y-auto p-1.5">
            <Command.Empty className="p-3 text-[12px] text-faint">{t("common.noResults")}</Command.Empty>
            {all.map((tag) => <Command.Item key={tag.id} value={tag.name} disabled={busy} onSelect={() => void change(value.includes(tag.id) ? value.filter((id) => id !== tag.id) : [...value, tag.id])} className="flex cursor-default items-center gap-2 rounded-md px-2 py-2 text-[12.5px] data-[selected=true]:bg-hover"><LabelDot color={tag.color} /><span className="flex-1 truncate">{tag.name}</span>{value.includes(tag.id) && <Check className="h-3.5 w-3.5 text-accent" />}</Command.Item>)}
            {canCreate && <Command.Item value={`create ${name}`} onSelect={() => void create()} disabled={busy} className="flex cursor-default items-center gap-2 rounded-md px-2 py-2 text-[12.5px] text-accent data-[selected=true]:bg-hover"><Plus className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{t("tags.create", { name })}</span></Command.Item>}
          </Command.List>
        </Command>
        {canCreate && <div className="flex gap-2 border-t border-border px-3 py-3" role="group" aria-label={t("tags.color")}>
          {COLORS.map((item, index) => <button key={item} type="button" disabled={busy} aria-label={t(`tags.colors.${index}`)} aria-pressed={color === item} className="flex h-6 w-6 items-center justify-center rounded-full" style={{ backgroundColor: item }} onClick={() => setColor(item)}>{color === item && <Check className="h-3.5 w-3.5 text-[#111113]" />}</button>)}
        </div>}
      </Popover.Content>
    </Popover.Portal>
  </Popover.Root>;
}
